import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Check, Copy, X } from "lucide-react";
import { toast } from "react-toastify";
import { accountApi, accountErr, TotpSetup } from "../../api/account/accountApi";

interface TotpSetupModalProps {
  onClose: () => void;
  onDone: () => void;
}

/**
 * Authenticator-app enrolment: Zitadel hands us a fresh secret, the user
 * scans it and proves possession with one code. Closing before verifying
 * leaves the factor in NOT_READY state; the next attempt simply restarts.
 */
const TotpSetupModal: React.FC<TotpSetupModalProps> = ({ onClose, onDone }) => {
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [loadError, setLoadError] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    accountApi
      .totpBegin()
      .then((r) => setSetup(r.data))
      .catch((e) => setLoadError(accountErr(e, "Could not start authenticator setup")));
  }, []);

  const copySecret = async () => {
    if (!setup) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — the secret is still visible */
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your app");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await accountApi.totpVerify(code);
      toast.success("Authenticator app enabled");
      onDone();
    } catch (err) {
      setError(accountErr(err, "That code was not accepted. Try the next one."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="acct-modal-overlay" onClick={onClose}>
      <form className="acct-modal" onClick={(e) => e.stopPropagation()} onSubmit={verify}>
        <div className="acct-modal__head">
          <h3>Set up authenticator app</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <p>
          Scan this QR code with Google Authenticator, Microsoft Authenticator,
          Authy or 1Password, then enter the 6-digit code it shows.
        </p>

        {loadError ? (
          <p className="acct-form-error">{loadError}</p>
        ) : !setup ? (
          <p className="acct-loading">Generating secret...</p>
        ) : (
          <div className="acct-qr">
            <QRCodeSVG value={setup.uri} size={180} level="M" includeMargin={false} />
            <div className="secret">
              <span>{setup.secret}</span>
              <button
                type="button"
                className="acct-btn acct-btn--small"
                onClick={copySecret}
                title="Copy secret (if you cannot scan)"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        )}

        <input
          className="code-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="000000"
          disabled={!setup}
        />
        {error && <p className="acct-form-error">{error}</p>}

        <div className="acct-actions">
          <button type="button" className="acct-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="acct-btn acct-btn--primary"
            disabled={busy || !setup}
          >
            {busy ? "Verifying..." : "Enable"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TotpSetupModal;
